// Ruta /saves — gestión de partidas guardadas (Sprint 5).
//
// - Carga lazy al montar.
// - Permite cargar (loadFromSave) o borrar (deleteSave) cada save.
// - Si IndexedDB no está disponible, muestra un mensaje al usuario.

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useGameStore, selectSaves, selectPersistenceAvailable } from '../store/gameStore';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import type { SavedGame } from '../lib/saveStore';

export const Route = createFileRoute('/saves')({
  component: SavesPage,
});

function SavesPage() {
  const saves = useGameStore(selectSaves);
  const available = useGameStore(selectPersistenceAvailable);
  const refreshSaves = useGameStore((s) => s.refreshSaves);
  const loadFromSave = useGameStore((s) => s.loadFromSave);
  const deleteSave = useGameStore((s) => s.deleteSave);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SavedGame | null>(null);

  useEffect(() => {
    refreshSaves().catch((err: unknown) => {
      console.warn('[saves] refresh error:', err);
    });
  }, [refreshSaves]);

  const handleLoad = async (id: string) => {
    setLoading(id);
    const ok = await loadFromSave(id);
    setLoading(null);
    if (ok) void navigate({ to: '/play' });
  };

  const handleDelete = async (save: SavedGame) => {
    setLoading(save.id);
    await deleteSave(save.id);
    setLoading(null);
    setConfirmDelete(null);
  };

  return (
    <main className="min-h-screen bg-bg text-text">
      <div className="border-b border-border bg-bg-alt/40">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 md:px-6">
          <Link to="/" className="text-sm text-text-muted transition hover:text-gold">
            ← Inicio
          </Link>
          <h1 className="font-display text-lg text-gold">Partidas guardadas</h1>
          <Link to="/play" className="text-sm text-gold transition hover:text-gold-soft">
            Jugar
          </Link>
        </div>
      </div>

      <section className="mx-auto max-w-5xl p-4 md:p-6">
        {!available && (
          <div
            className="rounded-xl border border-blood bg-blood/10 p-4 text-blood"
            role="alert"
            data-testid="no-persistence-warning"
          >
            <strong>Almacenamiento local no disponible.</strong>
            <p className="mt-1 text-sm text-text-muted">
              Tu navegador no soporta IndexedDB (modo privado o cookies deshabilitadas). Las
              partidas no se guardarán en este dispositivo.
            </p>
          </div>
        )}

        {available && saves.length === 0 && (
          <div
            className="rounded-xl border border-dashed border-border bg-bg-alt/40 p-8 text-center"
            data-testid="no-saves"
          >
            <p className="text-text-muted">No tenés partidas guardadas todavía.</p>
            <p className="mt-1 text-xs text-text-muted">
              Empezá una partida y se guardará automáticamente cada 30s.
            </p>
            <div className="mt-4">
              <Link to="/play">
                <Button>Ir a jugar</Button>
              </Link>
            </div>
          </div>
        )}

        {saves.length > 0 && (
          <ul
            className="grid gap-3"
            data-testid="saves-list"
            aria-label="Lista de partidas guardadas"
          >
            {saves.map((save, idx) => (
              <motion.li
                key={save.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: idx * 0.03 }}
                className="rounded-xl border border-border bg-bg-card p-4"
                data-testid={`save-${save.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-lg font-semibold text-gold">
                      {save.slotName ?? `Partida ${save.seed}`}
                    </h3>
                    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-text-muted sm:grid-cols-4">
                      <div>
                        <dt className="text-xs uppercase tracking-wider">Semilla</dt>
                        <dd className="font-mono text-text">{save.seed}</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wider">Vidas</dt>
                        <dd className="font-mono text-text">{save.lives}</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wider">Turno</dt>
                        <dd className="font-mono text-text">{save.history.length}</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wider">Fase</dt>
                        <dd className="text-text">
                          {save.phase === 'won'
                            ? 'Victoria'
                            : save.phase === 'lost'
                              ? 'Derrota'
                              : 'En curso'}
                        </dd>
                      </div>
                    </dl>
                    <p className="mt-2 text-xs text-text-muted">
                      Guardada:{' '}
                      <time dateTime={new Date(save.updatedAt).toISOString()}>
                        {new Date(save.updatedAt).toLocaleString()}
                      </time>
                    </p>
                  </div>

                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      disabled={loading !== null}
                      onClick={() => void handleLoad(save.id)}
                      data-testid={`load-save-${save.id}`}
                    >
                      Cargar
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={loading !== null}
                      onClick={() => setConfirmDelete(save)}
                      data-testid={`delete-save-${save.id}`}
                    >
                      Borrar
                    </Button>
                  </div>
                </div>
              </motion.li>
            ))}
          </ul>
        )}
      </section>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="¿Borrar partida?"
      >
        <p className="mb-4 text-text-muted">
          Vas a borrar{' '}
          <strong className="text-text">
            {confirmDelete?.slotName ?? `Partida ${confirmDelete?.seed ?? ''}`}
          </strong>
          . Esta acción no se puede deshacer.
        </p>
        <div className="flex gap-2">
          <Button
            variant="danger"
            onClick={() => confirmDelete && void handleDelete(confirmDelete)}
            data-testid="confirm-delete-btn"
          >
            Sí, borrar
          </Button>
          <Button
            variant="ghost"
            onClick={() => setConfirmDelete(null)}
            data-testid="cancel-delete-btn"
          >
            Cancelar
          </Button>
        </div>
      </Modal>
    </main>
  );
}
